import {
  Alert,
  Badge,
  Button,
  Card,
  SelectField,
  Table,
  TextField,
} from '../shared/components';

import authStyles from './ScreenLayout.module.css';
import styles from './ProgressDashboardPage.module.css';

/**
 * Committee view of how far the fund has got, and where to intervene.
 *
 * Finding one person among 872 is the job this screen is asked to do most
 * often, so it searches and filters — and corrects a record when the roster
 * itself is wrong. Approving declarations arrives with the next milestone
 * (spec FR-028).
 */

export interface ProgressTile {
  readonly labelAr: string;
  readonly value: number;
  readonly tone: 'neutral' | 'warning' | 'success';
}

export interface AttentionRow {
  readonly employeeId: string;
  readonly fullName: string;
  readonly employeeNumber: string;
  readonly branchNameAr: string;
  readonly reasonAr: string;
  readonly urgent: boolean;
}

export interface BranchOption {
  readonly id: string;
  readonly nameAr: string;
}

export interface DashboardFilters {
  /** An exact employee number. Empty searches nothing. */
  readonly employeeNumber: string;
  /** Matched against the loaded rows, not the whole roster. */
  readonly nameFragment: string;
  readonly branchId: string;
}

export interface PendingRow {
  readonly authUid: string;
  readonly fullNameAr: string;
  readonly email: string;
  readonly branchNameAr: string;
  readonly reasonAr: string;
}

export interface ProgressDashboardPageProps {
  readonly tiles: readonly ProgressTile[];
  readonly submittedPercent: number;
  readonly attention: readonly AttentionRow[];
  readonly pending: readonly PendingRow[];
  readonly loading: boolean;
  readonly errorAr: string | null;
  readonly noticeAr: string | null;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
  /**
   * Searching shows everyone matched; browsing shows who needs chasing.
   *
   * Looking a person up and being told they do not exist, because they had
   * finished and so were filtered out of the worklist, is worse than useless.
   */
  readonly searching: boolean;
  readonly branches: readonly BranchOption[];
  readonly filters: DashboardFilters;
  readonly onFiltersChange: (filters: DashboardFilters) => void;
  /** Null when this committee member may read but not correct. */
  readonly onEdit: ((employeeId: string) => void) | null;
}

export function ProgressDashboardPage({
  tiles,
  submittedPercent,
  attention,
  pending,
  loading,
  errorAr,
  noticeAr,
  hasMore,
  onLoadMore,
  searching,
  branches,
  filters,
  onFiltersChange,
  onEdit,
}: ProgressDashboardPageProps) {
  const set = <K extends keyof DashboardFilters>(key: K) => (value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className={authStyles.page}>
      <h1 className="visually-hidden">متابعة التقدم</h1>

      {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}
      {noticeAr ? <Alert severity="success">{noticeAr}</Alert> : null}

      <Card title="التقدم العام" description={`${String(submittedPercent)}% أكملوا التقديم`}>
        <div className={styles.tiles}>
          {tiles.map((tile) => (
            <div key={tile.labelAr} className={styles.tile}>
              <span className={styles.tileValue}>{tile.value}</span>
              <span className={styles.tileLabel}>{tile.labelAr}</span>
            </div>
          ))}
        </div>
      </Card>

      {pending.length > 0 ? (
        <Card
          title="طلبات بانتظار الربط"
          description="أشخاص سجّلوا بياناتهم ولم يُربطوا بسجل موظف بعد"
        >
          <Table
            caption="طلبات التسجيل المعلّقة"
            hideCaption
            columns={[
              { id: 'name', header: 'الاسم', cell: (row: PendingRow) => row.fullNameAr },
              { id: 'email', header: 'البريد', cell: (row: PendingRow) => row.email },
              { id: 'branch', header: 'الفرع', cell: (row: PendingRow) => row.branchNameAr },
              { id: 'reason', header: 'السبب', cell: (row: PendingRow) => row.reasonAr },
            ]}
            rows={pending}
            rowKey={(row) => row.authUid}
          />
        </Card>
      ) : null}

      <Card
        title={searching ? 'نتائج البحث' : 'يحتاجون متابعة'}
        description={
          searching
            ? 'كل من طابق البحث، وإن كان قد أنهى بياناته'
            : 'مرتّبون بحسب الأولوية، لا بحسب الرقم الوظيفي'
        }
      >
        <div className={styles.toolbar}>
          <TextField
            label="بحث بالرقم الوظيفي"
            numeric
            inputMode="numeric"
            value={filters.employeeNumber}
            onChange={(event) => {
              set('employeeNumber')(event.target.value);
            }}
            hint="يبحث في الكشف كله"
          />

          <TextField
            label="بحث بالاسم"
            value={filters.nameFragment}
            onChange={(event) => {
              set('nameFragment')(event.target.value);
            }}
            /* Asked of the server against the tokens stored on each record, so
               it reaches the whole roster and not just the loaded page. */
            hint="بأي جزء من الاسم"
          />

          <SelectField
            label="الفرع"
            value={filters.branchId}
            onChange={(event) => {
              set('branchId')(event.target.value);
            }}
          >
            <option value="">كل الفروع</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.nameAr}
              </option>
            ))}
          </SelectField>
        </div>
        {/* A list of 800 names sorted by employee number tells the committee
            nothing. What they need is who to chase, and why. */}
        <Table
          caption="الموظفون الذين يحتاجون متابعة"
          hideCaption
          loading={loading}
          emptyMessage={
            searching ? 'لا أحد يطابق هذا البحث.' : 'لا أحد يحتاج متابعة حاليًا.'
          }
          columns={[
            { id: 'name', header: 'الاسم', cell: (row: AttentionRow) => row.fullName },
            {
              id: 'number',
              header: 'الرقم الوظيفي',
              numeric: true,
              cell: (row: AttentionRow) => row.employeeNumber,
            },
            { id: 'branch', header: 'الفرع', cell: (row: AttentionRow) => row.branchNameAr },
            {
              id: 'reason',
              header: 'السبب',
              cell: (row: AttentionRow) => (
                <Badge tone={row.urgent ? 'error' : 'warning'} withDot>
                  {row.reasonAr}
                </Badge>
              ),
            },
            ...(onEdit === null
              ? []
              : [
                  {
                    id: 'edit',
                    header: 'تصحيح',
                    cell: (row: AttentionRow) => (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onEdit(row.employeeId);
                        }}
                      >
                        تعديل
                      </Button>
                    ),
                  },
                ]),
          ]}
          rows={attention}
          rowKey={(row) => row.employeeId}
        />

        {/* Pagination is not a nicety here: reading ~800 records on every open
            is a cost defect on a billing account with no hard cap. */}
        {hasMore ? (
          <div className={authStyles.actions}>
            <Button variant="secondary" fullWidth loading={loading} onClick={onLoadMore}>
              تحميل المزيد
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
